import { Link, createFileRoute } from '@tanstack/react-router'
import { ArrowRight } from 'lucide-react'
import { z } from 'zod'

import { useLive, useSessions, useSummary, useTimeseries } from '@/api/queries'
import type { TimeseriesBucket } from '@/api/types'
import { SessionStatusBadge } from '@/features/sessions/status-badge'
import { PeriodFilters, type PeriodFilterValue } from '@/features/filters/period-filters'
import { DynamicsChart } from '@/features/overview/dynamics-chart'
import { EmployeeRankingTable } from '@/features/overview/employee-ranking'
import { KpiCards } from '@/features/overview/kpi-cards'
import {
  Badge,
  Card,
  CardHeader,
  EmptyState,
  RecordingDot,
  Skeleton,
} from '@/components/ui/primitives'
import { dayRangeToIso, formatDateTime, formatPercent } from '@/shared/format'

const searchSchema = z.object({
  date_from: z.string().optional(),
  date_to: z.string().optional(),
  employee_id: z.string().optional(),
  location_id: z.string().optional(),
  bucket: z.enum(['day', 'week', 'month']).default('day'),
})

export const Route = createFileRoute('/_auth/')({
  validateSearch: searchSchema,
  component: OverviewPage,
})

function OverviewPage() {
  const search = Route.useSearch()
  const navigate = Route.useNavigate()
  const { me } = Route.useRouteContext()

  const filterValue: PeriodFilterValue = {
    date_from: search.date_from,
    date_to: search.date_to,
    employee_id: search.employee_id,
    location_id: search.location_id,
  }

  const apiFilters = {
    ...dayRangeToIso(search.date_from, search.date_to),
    employee_id: search.employee_id,
    location_id: search.location_id,
  }

  const summary = useSummary(apiFilters)
  const timeseries = useTimeseries({ ...apiFilters, bucket: search.bucket })
  const live = useLive()
  const recent = useSessions({ ...apiFilters, limit: 8 })

  const setFilters = (next: PeriodFilterValue) => {
    void navigate({ search: { ...next, bucket: search.bucket }, replace: true })
  }

  const setBucket = (bucket: TimeseriesBucket) => {
    void navigate({ search: { ...search, bucket }, replace: true })
  }

  return (
    <div className="space-y-5">
      <header className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="font-display text-xl font-bold text-ink">Обзор</h1>
          <p className="mt-0.5 text-[13px] text-ink-subtle">
            Здравствуйте, {me.full_name.split(' ')[0]}. Сводка по разговорам с клиентами.
          </p>
        </div>
        {live.data && live.data.active_count > 0 && (
          <Link to="/sessions" search={{ status: 'active' }}>
            <Badge tone="warning" className="cursor-pointer">
              <RecordingDot />
              Сейчас идёт разговоров: {live.data.active_count}
            </Badge>
          </Link>
        )}
      </header>

      <PeriodFilters user={me} value={filterValue} onChange={setFilters} />

      <KpiCards
        summary={summary.data}
        points={timeseries.data?.points ?? []}
        loading={summary.isLoading}
      />

      <DynamicsChart
        data={timeseries.data}
        loading={timeseries.isLoading}
        bucket={search.bucket}
        onBucketChange={setBucket}
      />

      <div className="grid grid-cols-1 gap-4 xl:grid-cols-2">
        <EmployeeRankingTable
          rankings={summary.data?.employee_rankings ?? []}
          loading={summary.isLoading}
        />

        <Card>
          <CardHeader
            title="Последние разговоры"
            actions={
              <Link
                to="/sessions"
                search={filterValue}
                className="inline-flex cursor-pointer items-center gap-1 text-[13px] font-medium text-brand-700 transition-colors hover:text-brand-500 dark:text-brand-300"
              >
                Все разговоры
                <ArrowRight className="size-3.5" />
              </Link>
            }
          />
          {recent.isLoading && !recent.data ? (
            <div className="space-y-2 p-4">
              {Array.from({ length: 5 }).map((_, index) => (
                <Skeleton key={index} className="h-9" />
              ))}
            </div>
          ) : (recent.data?.items.length ?? 0) === 0 ? (
            <EmptyState title="Нет разговоров за выбранный период" />
          ) : (
            <ul className="divide-y divide-line/60">
              {recent.data?.items.map((session) => (
                <li key={session.id}>
                  <Link
                    to="/sessions/$sessionId"
                    params={{ sessionId: session.id }}
                    className="flex cursor-pointer items-center gap-3 px-5 py-2.5 transition-colors hover:bg-surface-muted/60"
                  >
                    <div className="min-w-0 flex-1">
                      <p className="truncate text-[13px] font-medium text-ink">
                        {session.employee_full_name ?? 'Сотрудник не найден'}
                      </p>
                      <p className="text-xs text-ink-subtle">
                        {formatDateTime(session.started_at)}
                      </p>
                    </div>
                    <span className="font-mono text-[13px] text-ink-muted">
                      {formatPercent(session.script_compliance)}
                    </span>
                    <SessionStatusBadge status={session.status} />
                  </Link>
                </li>
              ))}
            </ul>
          )}
        </Card>
      </div>
    </div>
  )
}
